import { Controller } from "@hotwired/stimulus";

// Advanced responses export: pick a period, then the analyses inside it, and
// export them as a single CSV. Response counts are rendered per analysis and
// per provider so the total can be checked against the export limit here,
// before the request is sent.
export default class extends Controller {
  static targets = ["from", "to", "provider", "job", "ids", "summary", "error", "submit", "empty"];
  // maxAnalyses is optional: 0 means no limit on the number of analyses.
  static values = { max: Number, maxAnalyses: Number };

  connect() {
    this.applyPreset("month");
  }

  preset(event) {
    this.applyPreset(event.params.preset);
  }

  applyPreset(name) {
    const today = new Date();
    let from = "";
    let to = "";

    if (name === "month") {
      from = this.formatDate(new Date(today.getFullYear(), today.getMonth(), 1));
      to = this.formatDate(today);
    } else if (name === "last_month") {
      from = this.formatDate(new Date(today.getFullYear(), today.getMonth() - 1, 1));
      to = this.formatDate(new Date(today.getFullYear(), today.getMonth(), 0));
    } else if (name === "last_30_days") {
      from = this.formatDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29));
      to = this.formatDate(today);
    }

    this.fromTarget.value = from;
    this.toTarget.value = to;
    this.periodChanged();
  }

  // Changing the period selects every analysis inside it.
  periodChanged() {
    const from = this.fromTarget.value;
    const to = this.toTarget.value;

    this.jobTargets.forEach((job) => {
      const date = job.dataset.date;
      const inPeriod = (!from || date >= from) && (!to || date <= to);
      job.hidden = !inPeriod;
      this.checkbox(job).checked = inPeriod;
    });

    this.emptyTarget.classList.toggle("hidden", this.visibleJobs().length > 0);
    this.update();
  }

  selectAll() {
    this.visibleJobs().forEach((job) => (this.checkbox(job).checked = true));
    this.update();
  }

  selectNone() {
    this.visibleJobs().forEach((job) => (this.checkbox(job).checked = false));
    this.update();
  }

  update() {
    const provider = (this.hasProviderTarget && this.providerTarget.value) || "all";
    let total = 0;

    this.jobTargets.forEach((job) => {
      const count = this.countFor(job, provider);
      job.querySelector("[data-count]").textContent = count.toLocaleString("en-US");
      if (!job.hidden && this.checkbox(job).checked) total += count;
    });

    const selected = this.selectedJobs().length;
    const tooManyAnalyses = this.maxAnalysesValue > 0 && selected > this.maxAnalysesValue;
    const tooLarge = total > this.maxValue || tooManyAnalyses;

    this.summaryTarget.textContent = selected === 0
      ? "No analysis selected."
      : `${selected} ${selected === 1 ? "analysis" : "analyses"}, ${total.toLocaleString("en-US")} responses.`;

    if (tooManyAnalyses) {
      this.errorTarget.textContent = `Export too large: ${selected} analyses selected, the maximum is ${this.maxAnalysesValue}. Narrow the period or select fewer analyses.`;
    } else {
      this.errorTarget.textContent = tooLarge
        ? `Export too large: ${total.toLocaleString("en-US")} responses selected, the maximum is ${this.maxValue.toLocaleString("en-US")}. Narrow the period or select fewer analyses.`
        : "";
    }
    this.errorTarget.classList.toggle("hidden", !tooLarge);

    this.submitTarget.disabled = selected === 0 || tooLarge;
  }

  submit(event) {
    if (this.submitTarget.disabled) {
      event.preventDefault();
      return;
    }

    this.idsTarget.value = this.selectedJobs().map((job) => job.dataset.jobId).join(",");
    // The file downloads in the background; the page stays where it is.
    setTimeout(() => this.close(), 0);
  }

  close() {
    this.element.closest("dialog")?.close();
  }

  visibleJobs() {
    return this.jobTargets.filter((job) => !job.hidden);
  }

  selectedJobs() {
    return this.visibleJobs().filter((job) => this.checkbox(job).checked);
  }

  checkbox(job) {
    return job.querySelector("input[type=checkbox]");
  }

  countFor(job, provider) {
    const counts = JSON.parse(job.dataset.counts);
    return counts[provider] || 0;
  }

  formatDate(date) {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${date.getFullYear()}-${month}-${day}`;
  }
}
